import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestService } from './rest.service';
import { Direction } from '@interfaces/directions';
import Fuse from 'fuse.js';

@Injectable({
  providedIn: 'root'
})
export class DirectionService {
  directions: Direction[] = [];

  // Observable para arquitectura reactiva
  private directionsSubject = new BehaviorSubject<Direction[]>([]);
  public directions$ = this.directionsSubject.asObservable();

  // Fuse.js para búsqueda inteligente
  private fuse?: Fuse<Direction>;

  constructor(private rest: RestService) {
  }

  /**
   * Getter para acceder a las direcciones
   */
  getDirections(): Direction[] {
    return this.directions;
  }

  /**
   * Observable para suscribirse a cambios en las direcciones
   */
  getDirections$(): Observable<Direction[]> {
    return this.directions$;
  }

  /**
   * Carga todas las direcciones desde el servidor (1000 direcciones)
   */
  async loadAllDirections(): Promise<void> {
    const resp = await this.rest.getDirectionsAll();
    if (resp?.ok && resp?.response) {
      console.log('directions from service', resp.response);
      // Asegurarse de que response sea un array
      this.directions = Array.isArray(resp.response.data) ? resp.response.data : [];
      this.sendDirections();
    } else {
      console.log('error loading directions', resp);
      // Si hay error, inicializar como array vacío
      this.directions = [];
      this.sendDirections();
    }
  }

  /**
   * Envía las direcciones actualizadas a los suscritos
   */
  private sendDirections() {
    // Asegurarse de que directions sea siempre un array
    if (!Array.isArray(this.directions)) {
      this.directions = [];
    }

    // Ordenar por fecha de creación descendente (más recientes primero)
    this.directions = [...this.directions].sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    });
    this.directionsSubject.next(this.directions);
    // Reinicializar Fuse.js cuando cambian las direcciones
    this.initializeFuse();
  }

  /**
   * Inicializa Fuse.js para búsqueda inteligente en direcciones
   */
  private initializeFuse() {
    if (this.directions.length === 0) {
      this.fuse = undefined;
      return;
    }

    // Preparar datos para Fuse.js con campos de búsqueda concatenados
    const searchableDirections = this.directions.map(d => ({
      ...d,
      searchText: this.getSearchableText(d)
    }));

    this.fuse = new Fuse(searchableDirections, {
      keys: ['searchText'],
      threshold: 0.3, // Permite errores de escritura (ajustable según necesidad)
      ignoreLocation: true,
      includeScore: true
    });
  }

  /**
   * Obtiene el texto de búsqueda concatenado de una dirección
   */
  private getSearchableText(direction: Direction): string {
    const parts: string[] = [];

    if (direction.title) {
      parts.push(direction.title);
    }
    if (direction.description) {
      parts.push(direction.description);
    }
    if (direction.fromAddress) {
      parts.push(direction.fromAddress);
    }
    if (direction.user?.name) {
      parts.push(direction.user.name);
    }

    return parts.join(' ').toLowerCase();
  }

  /**
   * Busca direcciones usando Fuse.js
   * @param searchText Texto de búsqueda
   * @param limit Límite de resultados (por defecto sin límite)
   * @returns Array de direcciones encontradas
   */
  searchDirections(searchText: string, limit?: number): Direction[] {
    if (!searchText || !searchText.trim() || !this.fuse) {
      return [];
    }

    const trimmedText = searchText.trim().toLowerCase();
    const results = this.fuse.search(trimmedText);
    const matchedDirections = results.map(r => r.item);

    if (limit && limit > 0) {
      return matchedDirections.slice(0, limit);
    }

    return matchedDirections;
  }

  /**
   * Actualiza o agrega una dirección a la lista
   */
  setDirectionInfo(direction: Direction) {
    const index = this.directions.findIndex(d => d.id === direction.id);

    if (index === -1) {
      // Si no existe, agregarlo
      this.directions.push(direction);
    } else {
      // Si existe, actualizar manteniendo propiedades existentes
      this.directions[index] = { ...this.directions[index], ...direction };
    }
    this.sendDirections();
  }

  /**
   * Elimina una dirección de la lista
   */
  removeDirection(directionId: number) {
    this.directions = this.directions.filter(d => d.id !== directionId);
    this.sendDirections();
  }

  /**
   * Agrega múltiples direcciones (útil después de búsqueda o carga inicial)
   */
  addDirections(directions: Direction[]) {
    directions.forEach(direction => {
      const index = this.directions.findIndex(d => d.id === direction.id);
      if (index === -1) {
        this.directions.push(direction);
      } else {
        // Si existe, actualizar
        this.directions[index] = { ...this.directions[index], ...direction };
      }
    });
    this.sendDirections();
  }

  /**
   * Obtiene una dirección por ID
   */
  getDirectionById(id: number): Direction | undefined {
    return this.directions.find(d => d.id === id);
  }
}

